import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Manifesto from "@/components/Manifesto";
import CollectionSection from "@/components/CollectionSection";
import FinishPalette from "@/components/FinishPalette";
import HardwareSection from "@/components/HardwareSection";
import DoorDesignSection from "@/components/DoorDesignSection";
import GallerySection from "@/components/GallerySection";
import EndCTA from "@/components/EndCTA";
import Footer from "@/components/Footer";
import ChatWidget from "@/components/ChatWidget";
import { EXTERIOR_SERIES, INTERIOR_SERIES, GARAGE_SERIES } from "@/lib/catalog";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuote } from "@/context/QuoteContext";

export default function Landing() {
  const { hash, search, pathname } = useLocation();
  const navigate = useNavigate();
  const { openQuote } = useQuote();

  // Deep-link support: /?quote=1&email=xxx opens the Request Quote modal
  // pre-filled with the applicant's email. Used from the approval email CTA.
  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.get("quote") !== "1") return;
    const email = params.get("email") || "";
    const name = params.get("name") || "";
    openQuote({ email, name });
    // Strip the query string so refreshing the page doesn't re-open the modal.
    navigate(pathname + (hash || ""), { replace: true });
  }, [search, pathname, hash, navigate, openQuote]);

  // Scroll to the hash-referenced section whenever it changes (e.g. arriving
  // from /collection/exterior via a nav link to /#interior). We delay a tick
  // so the target section is mounted before scrolling.
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    // Retry a few times in case the section mounts after Lenis initializes.
    let tries = 0;
    let timer;
    const tick = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (tries++ < 10) timer = setTimeout(tick, 60);
    };
    timer = setTimeout(tick, 80);
    return () => clearTimeout(timer);
  }, [hash]);

  return (
    <div className="relative min-h-screen bg-brand-bone" data-testid="landing-page">
        <Navbar />
        <Hero />
        <Manifesto />

        <CollectionSection
          id="exterior"
          eyebrow="Exterior"
          chapterNumber="01"
          title="Exterior."
          subtitle="Signature entrances."
          intro="Three finishes — Toscana (veneer), Monaco (aluminum-clad) and Milano (melamine). Pick a design from our library, then choose your finish inside the configurator. Fully custom in dimension."
          series={EXTERIOR_SERIES}
          theme="light"
          ctaHref="/collection/exterior"
          ctaLabel="Configure Exterior →"
          finishPreview
        />

        <CollectionSection
          id="interior"
          eyebrow="Interior"
          chapterNumber="02"
          title="Interior."
          subtitle="Every threshold, resolved."
          intro="Three finishes — Forma (melamine), Porto (natural wood veneer) and Siena (painted MDF). Pick a design from our library, then choose your finish inside the configurator. Preset widths (18 to 36 in) and heights (80, 84, 96, 108 in)."
          series={INTERIOR_SERIES}
          theme="light"
          finishPreview
        />

        <CollectionSection
          id="garage"
          eyebrow="Garage"
          chapterNumber="03"
          title="Garage."
          subtitle="Architectural presence."
          intro="Ten curated garage door styles — Denver, Rio, Denmark, Montreal, Wonderwall, Berlin, Monolight, Dark Knight, Aspen and Michigan. Fully custom in dimension, and finished to match your exterior door."
          series={GARAGE_SERIES}
          theme="light"
          showPalette
          cardAspect="aspect-[16/9]"
        />

        <DoorDesignSection />
        <FinishPalette />
        <HardwareSection />
        <GallerySection />
        <EndCTA />
        <Footer />
        <ChatWidget />
      </div>
  );
}
